/**
 * Metronome
 * ---------------------
 * - Timing: Web Audio scheduler pattern (stable audio clock)
 * - Dynamic accent via Euclidean stress levels: primary / secondary / unstressed
 * - Role pitch comes from the three stress-level pitch sliders.
 */

import { el } from './modules/dom.js';
import { clamp, s3 } from './modules/utils.js';
import {
    buildStressLevels,
    maxRoundCount,
    modeCount,
    mod,
    reverseOffset,
} from './modules/rhythm-core.js';
import { initPresets } from './modules/presets.js';
import { createRhythm } from './modules/rhythm.js';
import {
    initAudio,
    ensureAudio,
    unlockAudioFromGesture,
    setMasterGainDb,
    getAudioCtx,
} from './modules/audio.js';
import { createUi } from './modules/ui.js';
import { initControls } from './modules/controls.js';
import { createScheduler } from './modules/scheduler.js';
import { downloadMidiLoop } from './modules/midi.js';

// -------------------------
// Utilities
// -------------------------

const BPM_MIN = 20;
const BPM_MAX = 300;
const ui = createUi(el, { BPM_MIN, BPM_MAX });

// -------------------------
// Audio engine state
// -------------------------

const LOOKAHEAD_MS = 25;
let scheduleAheadTime = 0.1;

let isRunning = false;
/** @type {number | null} */
let schedulerTimerId = null;
let startRequestSeq = 0;

function getScheduleAheadTime() {
    return scheduleAheadTime;
}

// Musical state
let bpm = 120;
let beatsPerBar = 4;
let subdivision = 1;
let swingPct = 0;

// Presets
let applyingPreset = false;

function clearPresetSelection() {
    if (!el.presetSelect || applyingPreset) return;
    el.presetSelect.value = '';
}

/** @param {boolean} value */
function setApplyingPreset(value) {
    applyingPreset = value;
}

function getState() {
    return {
        beatsPerBar,
        subdivision,
        secondaryPulses,
        secondaryRounds,
        secondaryMode,
        primaryPulses,
        primaryRounds,
        primaryMode,
    };
}

/** @param {Partial<{ beatsPerBar: number, subdivision: number, secondaryPulses: number, secondaryRounds: number, secondaryMode: number, primaryPulses: number, primaryRounds: number, primaryMode: number }>} next */
function setState(next) {
    if ('beatsPerBar' in next) beatsPerBar = next.beatsPerBar;
    if ('subdivision' in next) subdivision = next.subdivision;
    if ('secondaryPulses' in next) secondaryPulses = next.secondaryPulses;
    if ('secondaryRounds' in next) secondaryRounds = next.secondaryRounds;
    if ('secondaryMode' in next) secondaryMode = next.secondaryMode;
    if ('primaryPulses' in next) primaryPulses = next.primaryPulses;
    if ('primaryRounds' in next) primaryRounds = next.primaryRounds;
    if ('primaryMode' in next) primaryMode = next.primaryMode;
}

/** @param {number} newPct */
function setSwing(newPct) {
    swingPct = clamp(Math.round(newPct), 0, 80);
    el.swingLabel.textContent = String(swingPct);
    clearPresetSelection();
}

/** @param {number} nextBpm */
function setBpm(nextBpm) {
    const clamped = clamp(Math.round(nextBpm), BPM_MIN, BPM_MAX);
    bpm = clamped;
    ui.updateTempoUI(clamped);
    clearPresetSelection();
}

/** @param {number} db */
function setVolumeDb(db) {
    const clamped = clamp(Math.round(db), -36, 0);
    el.volLabel.textContent = String(clamped);
    setMasterGainDb(clamped);
}

const state = {
    get bpm() {
        return bpm;
    },
    set bpm(v) {
        bpm = v;
    },
    get beatsPerBar() {
        return beatsPerBar;
    },
    set beatsPerBar(v) {
        beatsPerBar = v;
    },
    get subdivision() {
        return subdivision;
    },
    set subdivision(v) {
        subdivision = v;
    },
    get swingPct() {
        return swingPct;
    },
    set swingPct(v) {
        swingPct = v;
    },
    get downbeatEnabled() {
        return downbeatEnabled;
    },
    set downbeatEnabled(v) {
        downbeatEnabled = v;
    },
    get secondaryPulses() {
        return secondaryPulses;
    },
    set secondaryPulses(v) {
        secondaryPulses = v;
    },
    get secondaryRounds() {
        return secondaryRounds;
    },
    set secondaryRounds(v) {
        secondaryRounds = v;
    },
    get secondaryMode() {
        return secondaryMode;
    },
    set secondaryMode(v) {
        secondaryMode = v;
    },
    get primaryPulses() {
        return primaryPulses;
    },
    set primaryPulses(v) {
        primaryPulses = v;
    },
    get primaryRounds() {
        return primaryRounds;
    },
    set primaryRounds(v) {
        primaryRounds = v;
    },
    get primaryMode() {
        return primaryMode;
    },
    set primaryMode(v) {
        primaryMode = v;
    },
    get primaryStressVolPct() {
        return primaryStressVolPct;
    },
    set primaryStressVolPct(v) {
        primaryStressVolPct = v;
    },
    get secondaryStressVolPct() {
        return secondaryStressVolPct;
    },
    set secondaryStressVolPct(v) {
        secondaryStressVolPct = v;
    },
    get unstressedVolPct() {
        return unstressedVolPct;
    },
    set unstressedVolPct(v) {
        unstressedVolPct = v;
    },
    get primaryPitchHz() {
        return primaryPitchHz;
    },
    set primaryPitchHz(v) {
        primaryPitchHz = v;
    },
    get secondaryPitchHz() {
        return secondaryPitchHz;
    },
    set secondaryPitchHz(v) {
        secondaryPitchHz = v;
    },
    get unstressedPitchHz() {
        return unstressedPitchHz;
    },
    set unstressedPitchHz(v) {
        unstressedPitchHz = v;
    },
    get primaryDecayMs() {
        return primaryDecayMs;
    },
    set primaryDecayMs(v) {
        primaryDecayMs = v;
    },
    get secondaryDecayMs() {
        return secondaryDecayMs;
    },
    set secondaryDecayMs(v) {
        secondaryDecayMs = v;
    },
    get unstressedDecayMs() {
        return unstressedDecayMs;
    },
    set unstressedDecayMs(v) {
        unstressedDecayMs = v;
    },
};

// Scheduling state
let nextEventTime = 0;
let stepIndex = 0;

// -------------------------
// Accent domain parameters
// -------------------------

// Pitch accent (base frequency per stress level, in Hz)
let primaryPitchHz = 1200;
let secondaryPitchHz = 880;
let unstressedPitchHz = 660;

// Duration accent (base decay per stress level, in milliseconds)
let primaryDecayMs = 60;
let secondaryDecayMs = 40;
let unstressedDecayMs = 25;

// Optional low downbeat indicator (beat 1 of bar)
let downbeatEnabled = false;

// -------------------------
// Euclidean rhythm state
// -------------------------

let secondaryPulses = 4;
let secondaryRounds = 0;
let secondaryMode = 0;

// Primary stress: pulses over the secondary stress (0 disables)
let primaryPulses = 1;
let primaryRounds = 0;
let primaryMode = 0;

// Volume overlays (0..100). Mapping uses a perceptual dB curve.
// These correspond to the Volume accent.
let primaryStressVolPct = 100; // Stress level 2: primary stress (X)
let unstressedVolPct = 50; // Stress level 0: unstressed (.)
let secondaryStressVolPct = 75; // Stress level 1: secondary stress volume (x)

// Per-step stress level: 0=unstressed, 1=secondary, 2=primary
/** @type {number[]} */
let stressLevels = []; // length stepsPerBar
/** @type {number | null} */
let lastStepInBar = null;

const rhythm = createRhythm({
    el,
    ui,
    state,
    clamp,
    maxRoundCount,
    modeCount,
    reverseOffset,
    mod,
    buildStressLevels,
    getLastStepInBar: () => lastStepInBar,
    setStressLevels: (levels) => {
        stressLevels = levels;
    },
});

const stepsPerBar = rhythm.stepsPerBar;
const updateRhythm = rhythm.updateRhythm;
const applyReverse = rhythm.applyReverse;
const applyFlip = rhythm.applyFlip;
const applyInvertSecondary = rhythm.applyInvertSecondary;

// -------------------------
// Pattern transforms (mode-based)
// -------------------------

// -------------------------
// Role mapping (no timbre accent)
// -------------------------

/** @param {"bar" | "beat" | "sub"} _role @param {number} baseHz */
function roleFrequencyHz(_role, baseHz) {
    return baseHz;
}

/** @param {"bar" | "beat" | "sub"} _role @param {number} baseDecaySec */
function roleDecaySeconds(_role, baseDecaySec) {
    return baseDecaySec;
}

// -------------------------
// Scheduler loop
// -------------------------

const scheduler = createScheduler({
    getTempo: () => ({ bpm, beatsPerBar, subdivision, swingPct }),
    getScheduleAheadTime: () => scheduleAheadTime,
    /** @param {number} t */
    setNextEventTime: (t) => {
        nextEventTime = t;
    },
    getNextEventTime: () => nextEventTime,
    /** @param {number} v */
    setStepIndex: (v) => {
        stepIndex = v;
    },
    getStepIndex: () => stepIndex,
    getStressLevels: () => stressLevels,
    getDownbeatEnabled: () => downbeatEnabled,
    getAudioParams: () => ({
        primaryPitchHz,
        secondaryPitchHz,
        unstressedPitchHz,
        primaryDecayMs,
        secondaryDecayMs,
        unstressedDecayMs,
        primaryStressVolPct,
        secondaryStressVolPct,
        unstressedVolPct,
        roleFrequencyHz,
        roleDecaySeconds,
    }),
    /** @param {{ stepInBar: number, role: "bar" | "beat" | "sub", delayMs: number }} evt */
    onVisual: ({ stepInBar, role, delayMs }) => {
        window.setTimeout(() => {
            const beat = Math.floor(stepInBar / subdivision) + 1;
            ui.renderBeatLights(beat, { role });
            if (Number.isInteger(stepInBar)) {
                lastStepInBar = stepInBar;
                ui.highlightRhythmStep(stepInBar);
            }
        }, delayMs);
    },
});

// -------------------------
// Transport controls
// -------------------------

function start() {
    if (isRunning) return;
    ensureAudio();
    unlockAudioFromGesture();
    const audioCtx = getAudioCtx();
    if (!audioCtx) return;
    const requestId = ++startRequestSeq;

    const startNow = () => {
        if (requestId !== startRequestSeq || isRunning) return;
        isRunning = true;
        if (schedulerTimerId) {
            window.clearInterval(schedulerTimerId);
            schedulerTimerId = null;
        }
        const now = audioCtx.currentTime;
        nextEventTime = now + 0.05;
        schedulerTimerId = window.setInterval(scheduler.schedulerTick, LOOKAHEAD_MS);
        el.startStopBtn.textContent = 'Stop';
        el.startStopBtn.classList.remove('primary');
        el.startStopBtn.classList.remove('danger');
        el.startStopBtn.classList.add('debossed');
        el.startStopBtn.classList.add('debossedText');
        el.startStopBtn.classList.add('isOn');
        ui.logLine(`Start @ audioTime=${s3(now)}`);
    };

    if (audioCtx.state === 'suspended') {
        audioCtx.resume().then(startNow).catch(startNow);
    } else {
        startNow();
    }
}

function stop() {
    startRequestSeq += 1;
    isRunning = false;
    if (schedulerTimerId) {
        window.clearInterval(schedulerTimerId);
        schedulerTimerId = null;
    }

    el.startStopBtn.textContent = 'Start';
    el.startStopBtn.classList.add('primary');
    el.startStopBtn.classList.remove('danger');
    el.startStopBtn.classList.remove('debossed');
    el.startStopBtn.classList.remove('debossedText');
    el.startStopBtn.classList.remove('isOn');
    ui.renderBeatLights(0, { role: 'sub' });
    ui.clearRhythmHighlight();
    ui.logLine('Stop');
}

function reset() {
    const audioCtx = getAudioCtx();
    if (!audioCtx) {
        stepIndex = 0;
        ui.renderBeatLights(1, { role: 'bar' });
        ui.logLine('Reset (no audio yet)');
        return;
    }

    const now = audioCtx.currentTime;
    stepIndex = 0;
    nextEventTime = now + 0.05;

    ui.renderBeatLights(1, { role: 'bar' });
    ui.clearRhythmHighlight();
    ui.logLine(`Reset @ audioTime=${s3(now)}`);
}

function toggleStartStop() {
    if (isRunning) stop();
    else start();
}

function exportMidi() {
    downloadMidiLoop({
        levels: stressLevels,
        beatsPerBar,
        subdivision,
        bpm,
    });
}

initAudio({ el, logLine: ui.logLine, getScheduleAheadTime });

// -------------------------
// Initial setup
// -------------------------

function init() {
    bpm = parseInt(el.bpmNumber.value, 10);
    beatsPerBar = parseInt(el.beatsPerBar.value, 10);
    subdivision = parseInt(el.subdivision.value, 10);
    swingPct = 0;
    if (el.presetSelect) el.presetSelect.value = '';

    el.bpmNumber.min = String(BPM_MIN);
    el.bpmNumber.max = String(BPM_MAX);

    // Secondary Stress Defaults (half for simple, third for triplets)
    const steps = stepsPerBar();
    const isTripletSubdivision = subdivision === 3;
    secondaryPulses = Math.max(1, Math.round(steps / (isTripletSubdivision ? 3 : 2)));
    el.secondaryPulsesNumber.value = String(secondaryPulses);
    secondaryRounds = maxRoundCount(steps, secondaryPulses);
    el.secondaryRoundsNumber.value = String(secondaryRounds);

    el.secondaryModeNumber.value = String(secondaryMode + 1);
    unstressedVolPct = parseInt(el.unstressedVolRange.value, 10);
    el.unstressedVolLabel.textContent = String(unstressedVolPct);

    el.primaryPulsesNumber.value = String(primaryPulses);
    primaryRounds = maxRoundCount(Math.max(0, secondaryPulses), primaryPulses);
    el.primaryRoundsNumber.value = String(primaryRounds);
    el.primaryModeNumber.value = String(primaryMode + 1);

    // Pulse/mode inputs: mins are always 0 (maxes are dynamic and set in updateRhythm)
    el.secondaryPulsesNumber.min = '0';
    el.secondaryRoundsNumber.min = '0';
    el.secondaryModeNumber.min = '1';
    el.primaryPulsesNumber.min = '0';
    el.primaryRoundsNumber.min = '0';
    el.primaryModeNumber.min = '1';

    // Primary stress volume
    primaryStressVolPct = parseInt(el.primaryStressVolRange.value, 10);
    el.primaryStressVolLabel.textContent = String(primaryStressVolPct);

    // Secondary stress volume
    secondaryStressVolPct = parseInt(el.secondaryStressVolRange.value, 10);
    el.secondaryStressVolLabel.textContent = String(secondaryStressVolPct);

    // Downbeat tone defaults
    downbeatEnabled = false;
    el.downbeatBtn.textContent = 'Downbeat';
    el.downbeatBtn.classList.remove('primary');
    el.downbeatBtn.classList.remove('danger');
    el.downbeatBtn.classList.remove('isOn');
    el.downbeatBtn.classList.remove('debossed');
    el.downbeatBtn.classList.remove('debossedText');

    el.swingLabel.textContent = String(swingPct);
    setVolumeDb(-10);
    el.aheadLabel.textContent = String(Math.round(scheduleAheadTime * 1000));

    // Pitch accent (stress-level base frequencies)
    primaryPitchHz = parseInt(el.primaryPitchRange.value, 10);
    secondaryPitchHz = parseInt(el.secondaryPitchRange.value, 10);
    unstressedPitchHz = parseInt(el.unstressedPitchRange.value, 10);
    el.primaryPitchLabel.textContent = String(primaryPitchHz);
    el.secondaryPitchLabel.textContent = String(secondaryPitchHz);
    el.unstressedPitchLabel.textContent = String(unstressedPitchHz);

    // Duration accent (stress-level base decays)
    primaryDecayMs = parseInt(el.primaryDecayRange.value, 10);
    secondaryDecayMs = parseInt(el.secondaryDecayRange.value, 10);
    unstressedDecayMs = parseInt(el.unstressedDecayRange.value, 10);
    el.primaryDecayLabel.textContent = String(primaryDecayMs);
    el.secondaryDecayLabel.textContent = String(secondaryDecayMs);
    el.unstressedDecayLabel.textContent = String(unstressedDecayMs);

    ui.updateTempoUI(bpm);
    ui.buildBeatLights(beatsPerBar);
    updateRhythm();
    ui.renderBeatLights(0, { role: 'sub' });

    ui.logLine('Ready. Press Start or hit Space.');
}

init();
initPresets({
    el,
    getState,
    setState,
    setSwing,
    buildBeatLights: ui.buildBeatLights,
    updateRhythm,
    reset,
    logLine: ui.logLine,
    setApplyingPreset,
});
initControls({
    el,
    ui,
    state,
    setBpm,
    setSwing,
    setVolumeDb,
    updateRhythm,
    reset,
    toggleStartStop,
    exportMidi,
    clearPresetSelection,
    applyReverse,
    applyFlip,
    applyInvertSecondary,
    maxRoundCount,
    modeCount,
});
